import type { Family, FamilyChild, FamilyGroup, MembershipStatus, OwnerParticipant } from "@teamna/shared";
import { useState, type KeyboardEvent, type ReactNode } from "react";
import { Badge, Button, Field, InlineError, SelectField, Sheet, TextAreaField, showToast } from "../../components/index.ts";
import { useActiveBusiness, useOwnerMembers } from "@teamna/web-client/hooks/use-owner.ts";
import { useFamilies } from "@teamna/web-client/hooks/use-families.ts";
import { ApiError } from "@teamna/web-client/api/client.ts";
import { usePlaceParticipant } from "@teamna/web-client/hooks/use-setup.ts";
import { AdultEditor, toAge } from "../families/editors.tsx";
import { capital, columnOf, familyName, type Words } from "../families/model.ts";
import type { FamilyActions } from "../families/types.ts";
import { OwnerParticipantPhoto } from "../participant-photo.tsx";
import { LinkFamilyPanel, linkWords } from "./link-family.tsx";
import { MembershipPanel } from "./panels.tsx";

const ACCESS_TONE = { active: "confirmed", pending: "late", paused: "quiet", left: "cancelled" } as const;
const ACCESS_WORDS: Record<MembershipStatus, string> = { active: "Active", pending: "Waiting for you", paused: "Paused", left: "Removed" };

/**
 * The Settings tab of a parent's page: the adults, each child (name, age, group, the notes, the
 * photo), a new child, the family's access to the business, the hold, and remove.
 * A name, a number and an age save when the field loses focus, the same as the Families editor did.
 */
export function FamilySettings({
  businessId,
  family,
  groups,
  participants,
  words,
  actions,
  onRemoved,
}: {
  businessId: string;
  family: Family;
  groups: FamilyGroup[];
  participants: OwnerParticipant[];
  words: Words;
  actions: FamilyActions;
  /** The family was removed: leave its page. The toast keeps the Undo. */
  onRemoved: () => void;
}) {
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [adultDraft, setAdultDraft] = useState<{ name: string; phone: string } | null>(null);
  const [childName, setChildName] = useState("");
  const [childAge, setChildAge] = useState("");
  const [holdDraft, setHoldDraft] = useState<string | null>(null);
  // "Link a parent who is already here": open, and the other family when the panel starts from a refused number.
  const [linking, setLinking] = useState<{ startWith: string | null } | null>(null);
  const [linkOffer, setLinkOffer] = useState<string | null>(null);
  // A link moves packages and payments: the owner and admins only (the API refuses the front desk).
  const { isFrontDesk } = useActiveBusiness();
  const allFamilies = useFamilies(isFrontDesk ? null : businessId);
  const memberships = useOwnerMembers(businessId);
  const membership = memberships.data?.find((row) => row.userId === family.id) ?? null;
  const [access, setAccess] = useState<MembershipStatus | null>(null);
  const name = familyName(family);
  const unit = linkWords(words);

  async function run(work: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await work();
    } catch (failure) {
      setError(failure);
    } finally {
      setBusy(false);
    }
  }

  function addChild() {
    const next = childName.trim();
    if (!next) return;
    void run(async () => {
      await actions.addChild(family.id, { name: next, age: toAge(childAge) });
      setChildName("");
      setChildAge("");
      showToast(`${next} added`);
    });
  }

  function onChildKey(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") {
      event.preventDefault();
      addChild();
    }
  }

  function removeFamily() {
    void run(async () => {
      const removed = await actions.removeFamily(family.id);
      showToast(`${words.forChildren ? `The family of ${name}` : name} removed.`, {
        action: { label: "Undo", onClick: () => void actions.restoreFamily(removed).catch(setError) },
      });
      onRemoved();
    });
  }

  return (
    <div className="flex flex-col gap-6">
      {error ? <InlineError error={error} /> : null}

      <SettingsSection title={capital(words.members)} description="The name and the number change here only until the person signs in. Then only they can change them.">
        {family.adults.map((adult) => (
          <AdultEditor key={adult.userId} family={family} adult={adult} actions={actions} onError={setError} />
        ))}
        {adultDraft ? (
          <div className="flex flex-wrap items-end gap-2">
            <Field
              size="compact"
              label={`${capital(words.member)} name`}
              value={adultDraft.name}
              onChange={(event) => setAdultDraft({ ...adultDraft, name: event.target.value })}
              className="min-w-[160px]"
              dir="auto"
            />
            <Field
              size="compact"
              label="Mobile number"
              type="tel"
              inputMode="tel"
              value={adultDraft.phone}
              onChange={(event) => setAdultDraft({ ...adultDraft, phone: event.target.value })}
              className="min-w-[150px]"
            />
            <Button
              variant="secondary"
              loading={busy}
              disabled={!adultDraft.name.trim()}
              onClick={() =>
                void run(async () => {
                  setLinkOffer(null);
                  try {
                    await actions.addAdult(family.id, {
                      name: adultDraft.name.trim(),
                      ...(adultDraft.phone.trim() ? { phone: adultDraft.phone.trim() } : {}),
                    });
                  } catch (failure) {
                    // The number is a family of its own here: offer to link the two families.
                    const other = failure instanceof ApiError ? failure.meta?.linkFamilyId : undefined;
                    if (typeof other === "string" && !isFrontDesk) setLinkOffer(other);
                    throw failure;
                  }
                  setAdultDraft(null);
                })
              }
            >
              Save
            </Button>
            <Button variant="ghost" onClick={() => setAdultDraft(null)}>
              Cancel
            </Button>
          </div>
        ) : family.adults.length < 4 ? (
          <div className="flex flex-wrap gap-2">
            <Button variant="ghost" onClick={() => setAdultDraft({ name: "", phone: "" })}>
              Add another {words.member}
            </Button>
            {!isFrontDesk ? (
              <Button variant="ghost" onClick={() => setLinking({ startWith: null })}>
                Link a {words.member} who is already here
              </Button>
            ) : null}
          </div>
        ) : null}
        {linkOffer ? (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[14px] text-ink-2">
              This number is {words.forChildren ? "a family" : "a client"} here already. You can link the two {linkWords(words).many}.
            </span>
            <Button
              variant="secondary"
              onClick={() => {
                setLinking({ startWith: linkOffer });
                setLinkOffer(null);
                setAdultDraft(null);
                setError(null);
              }}
            >
              Link the {linkWords(words).many}
            </Button>
          </div>
        ) : null}
      </SettingsSection>

      {linking ? (
        <LinkFamilyPanel
          family={family}
          families={allFamilies.data?.families ?? []}
          words={words}
          actions={actions}
          startWith={linking.startWith}
          onClose={() => setLinking(null)}
        />
      ) : null}

      <SettingsSection title={capital(words.participants)}>
        {family.children.map((child) => (
          <ChildSettings
            key={child.participantId}
            businessId={businessId}
            family={family}
            child={child}
            participant={participants.find((row) => row.id === child.participantId) ?? null}
            groups={groups}
            words={words}
            actions={actions}
            onError={setError}
          />
        ))}
        <div className="flex flex-wrap items-end gap-2">
          <Field
            size="compact"
            label={`New ${words.participant}`}
            value={childName}
            onChange={(event) => setChildName(event.target.value)}
            onKeyDown={onChildKey}
            className="min-w-[160px]"
            dir="auto"
          />
          <Field
            size="compact"
            label="Age, optional"
            inputMode="numeric"
            value={childAge}
            onChange={(event) => setChildAge(event.target.value)}
            onKeyDown={onChildKey}
            className="w-[110px]"
          />
          <Button variant="secondary" onClick={addChild} disabled={!childName.trim()} loading={busy}>
            Add {words.participant}
          </Button>
        </div>
      </SettingsSection>

      {membership ? (
        <SettingsSection title="Access">
          <div className="flex flex-wrap items-center gap-3">
            <Badge tone={ACCESS_TONE[membership.status]}>{ACCESS_WORDS[membership.status]}</Badge>
            <span className="min-w-0 flex-1 text-[14px] text-ink-2">
              {membership.status === "paused"
                ? "Paused: out of your active list. Balances stay as they are."
                : membership.status === "left"
                  ? "Removed: no access to your schedule. Past bookings and payments stay on record."
                  : `Can see your schedule and book for their ${words.participants}.`}
            </span>
            {membership.status === "active" ? (
              <Button variant="secondary" onClick={() => setAccess("paused")}>
                Pause
              </Button>
            ) : null}
            {membership.status === "paused" ? (
              <Button variant="secondary" onClick={() => setAccess("active")}>
                Resume
              </Button>
            ) : null}
            {membership.status !== "left" ? (
              <Button variant="ghost" onClick={() => setAccess("left")}>
                Remove access
              </Button>
            ) : null}
          </div>
        </SettingsSection>
      ) : null}

      <SettingsSection title="Danger zone" description={`These actions stop work with this ${unit.one} or take it off your lists.`} tone="danger">
        <DangerRow
          title={family.holdNote ? "On hold" : "Put on hold"}
          text={
            family.holdNote
              ? `Your note: ${family.holdNote}`
              : `Stop work on this ${unit.one} for now. The note tells you why. Find it under "On hold" on the ${capital(words.members)} page.`
          }
        >
          {holdDraft !== null ? (
            <>
              <div className="min-w-[260px] flex-1">
                <Field
                  size="compact"
                  label="Note for later"
                  placeholder="Why you stop here, for example: waiting for the father's number"
                  value={holdDraft}
                  onChange={(event) => setHoldDraft(event.target.value)}
                  className="w-full"
                  dir="auto"
                />
              </div>
              <Button
                variant="secondary"
                loading={busy}
                onClick={() =>
                  void run(async () => {
                    await actions.setHold(family.id, holdDraft.trim() || "Come back to this");
                    setHoldDraft(null);
                    showToast(`On hold. Find it under "On hold" on the ${capital(words.members)} page.`);
                  })
                }
              >
                Save
              </Button>
              <Button variant="ghost" onClick={() => setHoldDraft(null)}>
                Cancel
              </Button>
            </>
          ) : family.holdNote ? (
            <Button variant="secondary" loading={busy} onClick={() => void run(() => actions.setHold(family.id, null))}>
              End the hold
            </Button>
          ) : (
            <Button variant="secondary" onClick={() => setHoldDraft("")}>
              Put on hold with a note
            </Button>
          )}
        </DangerRow>
        <DangerRow
          title={`Remove the ${unit.one}`}
          text={`${words.forChildren ? `The family and its ${words.participants} go` : `${name} goes`} off your lists. Undo shows for a short time after you remove.`}
        >
          {/* No confirm step: the toast carries Undo, the same as the Families page had. */}
          <Button variant="danger" onClick={removeFamily} loading={busy}>
            Remove the {unit.one}
          </Button>
        </DangerRow>
      </SettingsSection>

      {access && membership ? (
        <MembershipPanel businessId={businessId} membershipId={membership.id} name={name} status={access} onClose={() => setAccess(null)} />
      ) : null}
    </div>
  );
}

function SettingsSection({
  title,
  description,
  tone = "default",
  children,
}: {
  title: string;
  description?: string;
  /** `danger` for the actions that stop work with the family or remove it: a red frame and title. */
  tone?: "default" | "danger";
  children: ReactNode;
}) {
  const danger = tone === "danger";
  return (
    <section
      aria-label={title}
      className={`flex flex-col gap-3 rounded-card border bg-paper px-5 py-4 shadow-card ${danger ? "border-cancelled/50" : "border-line"}`}
    >
      <div>
        <h2 className={`text-[17px] ${danger ? "text-cancelled-ink" : ""}`}>{title}</h2>
        {description ? <p className="mt-1 text-[13px] text-ink-2">{description}</p> : null}
      </div>
      {children}
    </section>
  );
}

/** One action of the danger zone: what it does on the left, its buttons on the right. */
function DangerRow({ title, text, children }: { title: string; text: string; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-end gap-x-4 gap-y-2 border-t border-cancelled/20 pt-3">
      <div className="min-w-[220px] flex-1">
        <h3 className="text-[15px] font-semibold text-ink">{title}</h3>
        <p className="mt-0.5 text-[13px] text-ink-2" dir="auto">
          {text}
        </p>
      </div>
      <div className="flex flex-wrap items-end gap-2">{children}</div>
    </div>
  );
}

/** One child: name and age save on blur, the group saves on change, the note on Save. */
function ChildSettings({
  businessId,
  family,
  child,
  participant,
  groups,
  words,
  actions,
  onError,
}: {
  businessId: string;
  family: Family;
  child: FamilyChild;
  participant: OwnerParticipant | null;
  groups: FamilyGroup[];
  words: Words;
  actions: FamilyActions;
  onError: (error: unknown) => void;
}) {
  const place = usePlaceParticipant(businessId);
  // The front desk changes a photo only with Profile photos (Settings, Front desk), even on this tab.
  const { may } = useActiveBusiness();
  const [name, setName] = useState(child.nameMissing ? "" : child.name);
  const [age, setAge] = useState(child.age !== null ? String(child.age) : "");
  const [note, setNote] = useState(participant?.notes ?? "");
  const [photoOpen, setPhotoOpen] = useState(false);
  const savedNote = participant?.notes ?? "";

  function remove() {
    void actions
      .removeChild(family.id, child.participantId)
      .then(() =>
        showToast(`${child.name} removed.`, {
          action: { label: "Undo", onClick: () => void actions.restoreChild(family.id, child.participantId).catch(onError) },
        }),
      )
      .catch(onError);
  }

  const photo = (
    <Sheet open={photoOpen} onClose={() => setPhotoOpen(false)} title={`Profile photo of ${child.name}`}>
      <OwnerParticipantPhoto
        businessId={businessId}
        participantId={child.participantId}
        name={child.name}
        photoUrl={child.photoUrl ?? null}
        photosRefused={participant?.photosRefused === true}
        onDone={() => setPhotoOpen(false)}
      />
    </Sheet>
  );

  const buttons = (
    <div className="flex flex-wrap gap-2">
      {may("setPhoto") ? (
        <Button variant="ghost" onClick={() => setPhotoOpen(true)}>
          {child.photoUrl ? "Change photo" : "Add a photo"}
        </Button>
      ) : null}
      <span className="flex-1" />
      <Button variant="ghost" onClick={remove}>
        Remove {child.name}
      </Button>
    </div>
  );

  // The person's own record that this business does not hold (D36): it is the same record at every
  // business they are at, so the API refuses a change here (D26). The photo is this business's own,
  // and Remove takes the person off this business's lists only.
  if (child.heldHere === false) {
    return (
      <div className="flex flex-col gap-3 rounded-xl bg-surface px-4 py-3">
        <p className="font-semibold text-ink" dir="auto">
          {child.name}
        </p>
        <p className="text-[13px] text-ink-2">
          This person can book at other businesses too. You cannot change their details here. They change their name in the
          app. Remove takes them off your list only.
        </p>
        {buttons}
        {photo}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 rounded-xl bg-surface px-4 py-3">
      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-[150px] flex-1">
          <Field
            size="compact"
            label={child.nameMissing ? `Name (now: ${child.name})` : "Name"}
            placeholder={child.nameMissing ? `Type the ${words.participant}'s name` : undefined}
            value={name}
            onChange={(event) => setName(event.target.value)}
            onBlur={() => {
              const next = name.trim();
              if (next && next !== child.name) void actions.updateChild(family.id, child.participantId, { name: next }).catch(onError);
            }}
            className="w-full"
            dir="auto"
          />
        </div>
        <Field
          size="compact"
          label="Age"
          inputMode="numeric"
          value={age}
          onChange={(event) => setAge(event.target.value)}
          onBlur={() => {
            const next = toAge(age);
            if (next !== child.age) void actions.updateChild(family.id, child.participantId, { age: next }).catch(onError);
          }}
          className="w-[90px]"
        />
        <SelectField
          size="compact"
          label={capital(words.group)}
          value={columnOf(child, groups)}
          onChange={(event) => {
            const value = event.target.value;
            void actions.place(child.participantId, value === "none" ? null : value).catch(onError);
          }}
          className="min-w-[170px]"
        >
          <option value="none">No {words.group} yet</option>
          {groups.map((group) => (
            <option key={group.id} value={group.id}>
              {group.name}
            </option>
          ))}
          <option value="unsure">Not sure yet</option>
        </SelectField>
      </div>
      {participant ? (
        <div className="flex flex-col gap-2">
          <TextAreaField
            label="Notes"
            hint={`Health, fears, who picks up. The ${words.member} sees this note too.`}
            value={note}
            maxLength={1000}
            rows={2}
            onChange={(event) => setNote(event.target.value)}
            dir="auto"
          />
          {note.trim() !== savedNote.trim() ? (
            <div>
              <Button
                variant="secondary"
                loading={place.isPending}
                onClick={() =>
                  place.mutate(
                    { id: child.participantId, notes: note.trim() || null },
                    { onSuccess: () => showToast("Note saved"), onError },
                  )
                }
              >
                Save the note
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
      {buttons}
      {photo}
    </div>
  );
}
